import compact from 'lodash/compact';
import sum from 'lodash/sum';
import { FC, memo, useMemo } from 'react';
import { MatchScore } from '@/components/tables/match-play/match-shared';
import { ElasticCourseData, ElasticMatchplayMatchesMatchesInner, ElasticMatchplayMatchesMatchesInnerPlayersInner, ElasticRoundAnalytics, ElasticTournament, MatchplayGroupsGroupsInnerTeamsInner, TournamentRequestFourpeopleTypesEnum } from '@spikemark/rest-api';
import { Mascot } from '@spikemark/ui/components/mascot';
import { HoleScorecard } from '@spikemark/ui/components/scorecard/hole-scorecard';
import { ScorecardDialogProvider } from '@spikemark/ui/components/scorecard/scorecard-dialog-provider';
import { ScorecardRow } from '@spikemark/ui/components/scorecard/types';
import { cn } from '@spikemark/ui/utils/cn';
import { formatNumberOrdinal, formatPlayerScore } from '@spikemark/ui/utils/format';
import { createCourseInfoMap } from '@spikemark/ui/utils/scoring';
import { ScorecardExpander } from './scorecard-expander';
type MatchSummaryPlayerResultProps = {
  side: 'left' | 'right';
  className?: string;
  score: string;
  name: string;
  lose: boolean;
  schoolLogo: string;
  schoolName?: string;
  schoolColor?: string;
  secondaryPlayer?: ElasticMatchplayMatchesMatchesInnerPlayersInner;
  playerImage?: string;
};
const NameBlock: FC<{
  name: string;
  lose: boolean;
  schoolLogo: string;
  schoolName?: string;
  schoolColor?: string;
  playerImage?: string;
}> = ({
  name,
  lose,
  schoolLogo,
  schoolName,
  schoolColor,
  playerImage
}) => {
  return <div className="flex flex-col items-center justify-center max-w-full flex-1 leading-tight text-xs md:text-sm lg:text-base xl:text-lg lg:h-36" data-sentry-component="NameBlock" data-sentry-source-file="match-summary.tsx">
      <Mascot path={playerImage ?? schoolLogo} variant="print" alt={name} className="hidden md:block" displayMode={typeof playerImage === 'string' ? 'player' : 'team'} borderColor={lose ? schoolColor : 'white'} data-sentry-element="Mascot" data-sentry-source-file="match-summary.tsx" />
      <Mascot path={playerImage ?? schoolLogo} variant="small" alt={name} className="md:hidden" displayMode={typeof playerImage === 'string' ? 'player' : 'team'} borderColor={lose ? schoolColor : 'white'} data-sentry-element="Mascot" data-sentry-source-file="match-summary.tsx" />
      <div className="text-center max-w-full mt-2 leading-tight">
        <div className={cn('font-bold', {
        'truncate lg:whitespace-nowrap': !!schoolName,
        'line-clamp-2': !schoolName
      })}>
          {name}
        </div>
        {schoolName ? <div className={cn('text-2xs md:text-xs lg:text-sm truncate', {
        'text-white': !lose,
        'text-gray-700': lose
      })}>
            {schoolName}
          </div> : null}
      </div>
    </div>;
};
export const MatchSummaryPlayerResult: FC<MatchSummaryPlayerResultProps> = memo(({
  side,
  className,
  score,
  name,
  lose,
  playerImage,
  schoolName,
  schoolLogo,
  schoolColor,
  secondaryPlayer
}) => {
  const nameSection = <div className="w-full flex justify-between">
        <NameBlock name={name} lose={lose} schoolLogo={schoolLogo} schoolName={schoolName} schoolColor={schoolColor} playerImage={playerImage} />
        {secondaryPlayer ? <NameBlock name={secondaryPlayer.playerName} lose={lose} schoolLogo={schoolLogo} schoolName={schoolName} schoolColor={schoolColor} playerImage={secondaryPlayer.playerImage} /> : null}
      </div>;
  const blueSvg = <div className="h-full w-3 lg:w-6 absolute left-0">
        <svg className="h-full w-full" viewBox="0 0 10 10" preserveAspectRatio="none">
          <path className="fill-white" d="M10,0 L0,0 L0,5 L0,10 L10,10 L0,5Z" />
        </svg>
      </div>;
  const redSvg = <div className="h-full w-3 lg:w-6 absolute right-0">
        <svg className="h-full w-full" viewBox="0 0 10 10" preserveAspectRatio="none">
          <path className="fill-white" d="M0,0 L10,0 L10,5 L10,10 L0,10 L10,5Z" />
        </svg>
      </div>;
  const leftSide = <>
        <div className="flex flex-col md:flex-row items-center justify-center border-l-4 lg:border-l-8 flex-1 max-w-full h-full pl-1 pr-3 lg:pr-6 py-2" style={{
      borderColor: schoolColor
    }}>
          {nameSection}
        </div>
        {redSvg}
      </>;
  const rightSide = <>
        {blueSvg}
        <div className="flex flex-col md:flex-row-reverse items-center border-r-4 lg:border-r-8 flex-1 justify-center max-w-full h-full pl-3 pr-1 lg:pl-6 py-2" style={{
      borderColor: schoolColor
    }}>
          {nameSection}
        </div>
      </>;
  return <div className={cn('flex relative items-center w-[30%] lg:w-1/4 shrink-0', className, {
    'text-white': !lose,
    'text-gray-600': lose
  })} style={{
    backgroundColor: lose ? undefined : schoolColor
  }}>
        {side === 'right' ? rightSide : leftSide}
      </div>;
});
type MatchSummaryProps = {
  format: 'Match Play' | 'Medal Match Play';
  matchplayFormat: TournamentRequestFourpeopleTypesEnum;
  tournament: ElasticTournament;
  match: ElasticMatchplayMatchesMatchesInner;
  teamA?: MatchplayGroupsGroupsInnerTeamsInner;
  teamB?: MatchplayGroupsGroupsInnerTeamsInner;
  courseSetup: ElasticCourseData[];
};
export const MatchSummary: FC<MatchSummaryProps> = memo(({
  format,
  match,
  teamA,
  teamB,
  courseSetup,
  tournament,
  matchplayFormat
}) => {
  match = useMemo(() => {
    // If the data is published, then we need to remove the holes that are not played.
    // For live data, we use `null` already for skipped holes, but once published, they
    // turn to zeros. If both players have a zero for a hole, we can assume it was skipped.
    return {
      ...match,
      players: match.players.map(player => {
        const strokes: (number | null)[] = player.strokes.map((strokes, i) => {
          const everyPlayerIsZero = match.players.every(y => !y.strokes[i]);
          return everyPlayerIsZero ? null : strokes;
        });
        return {
          ...player,
          strokes,
          scoresDetail: player.scoresDetail.map((score, i) => strokes[i] === null ? null : score)
        };
      })
    };
  }, [match]);
  const teamAPlayerIndex = match.players.findIndex(player => player.schoolId === teamA?.schoolId);
  if (teamAPlayerIndex === -1) {
    return <></>;
  }
  const playerA = match.players.find(player => player.schoolId === teamA?.schoolId);
  const playerB = match.players.find(player => player.schoolId === teamB?.schoolId);
  if (playerA && playerB) {
    const secondaryPlayerA = match.players.length === 4 ? match.players.findLast(player => player.schoolId === teamA?.schoolId) : undefined;
    const secondaryPlayerB = match.players.length === 4 ? match.players.findLast(player => player.schoolId === teamB?.schoolId) : undefined;
    const courseInfoMap = createCourseInfoMap((courseSetup as ElasticCourseData[]).map(course => {
      const holes = playerA.holes && playerA.holes.length > 0 ? playerA.holes : course.holes;
      return {
        ...course,
        pars: holes?.map(holeNumber => course.pars[holeNumber - 1]) ?? course.pars,
        yards: holes?.map(holeNumber => course.yards[holeNumber - 1]) ?? course.yards,
        holes: course.holes
      };
    }));
    const data: ScorecardRow[] | null = !!playerA.strokes ? compact([mapPlayer(playerA, teamA, match.startingHole), secondaryPlayerA ? mapPlayer(secondaryPlayerA, teamA, match.startingHole, match.teeTime) : false, {
      type: 'MATCHPLAY_DELTA',
      scores: playerA.scoresDetail,
      rounds: [{
        roundId: 1,
        startingHole: match.startingHole
      }]
    } as ScorecardRow, mapPlayer(playerB, teamB, match.startingHole), secondaryPlayerB ? mapPlayer(secondaryPlayerB, teamB, match.startingHole, match.teeTime) : false]) : null;
    const filterHolesCondition = x => x !== null && x !== undefined;
    const holesPlayed = playerA.scoresDetail.filter(filterHolesCondition).length;
    const matchHasStarted = match.players.some(x => x.scoresDetail.filter(filterHolesCondition).length > 0);
    const matchIsComplete = Math.abs(match.players[0].playerDetailUp) > match.players[0].holesLeft;
    return <div className="border border-gray-300 bg-white rounded-t-lg rounded-b-lg overflow-clip mb-4 shadow-md">
          <div className="flex w-full justify-center whitespace-nowrap">
            <MatchSummaryPlayerResult side="left" name={playerA.playerName} score={formatPlayerScore(playerA, format)} lose={playerA.playerResult !== 'Win'} schoolLogo={teamA?.schoolLogo || ''} schoolName={teamA?.schoolName} schoolColor={teamA?.schoolColour} secondaryPlayer={secondaryPlayerA} playerImage={playerA.playerImage} />
            <div className="flex-1 flex flex-col items-center justify-around p-2 lg:p-4 text-sm text-center text-gray-600 font-bold">
              <h3>Match {match.matchId}</h3>
              <MatchScore match={match} teamA={teamA} teamB={teamB} teeTime={match.teeTime} matchHasStarted={matchHasStarted} format={format} />
              {/* TODO: Need to know whether this is a result or not */}
              <h6>
                {matchIsComplete ? 'Result' : holesPlayed > 0 ? `${formatNumberOrdinal(holesPlayed)} Hole` : ''}
              </h6>
            </div>
            <MatchSummaryPlayerResult side="right" name={playerB.playerName} score={formatPlayerScore(playerB, format)} lose={playerB.playerResult !== 'Win'} schoolLogo={teamB?.schoolLogo || ''} schoolName={teamB?.schoolName} schoolColor={teamB?.schoolColour} secondaryPlayer={secondaryPlayerB} playerImage={playerB.playerImage} />
          </div>
          {data ? <ScorecardDialogProvider data={data} format={format} courseInfoMap={courseInfoMap}>
              <ScorecardExpander>
                <HoleScorecard tournament={tournament} courseInfoMap={courseInfoMap} eventType={format} matchplayFormat={matchplayFormat} mode="score" numRounds={1} roundId="1" data={data} suppressRank match={match} />
              </ScorecardExpander>
            </ScorecardDialogProvider> : null}
        </div>;
  } else {
    return <></>;
  }
});
function mapPlayer(player: ElasticMatchplayMatchesMatchesInnerPlayersInner, team?: MatchplayGroupsGroupsInnerTeamsInner, startingHole?: number, teeTime?: string): ElasticRoundAnalytics {
  return {
    playerId: player.playerId,
    playerName: player.playerName,
    userPicture: player.playerImage,
    rounds: [{
      roundId: 1,
      thru: String(player.strokes.length ?? '0'),
      courseLabel: 'A',
      scores: player.scores,
      strokes: player.strokes,
      startingHole,
      teeTime
    }],
    schoolId: player.schoolId,
    schoolName: team?.schoolName ?? '',
    schoolLogo: team?.schoolLogo,
    schoolColor: team?.schoolColour,
    teamLabel: player.teamLabel,
    scores: [sum(player.scores)] as number[],
    strokes: [sum(player.strokes)] as number[],
    holes: player.holes,
    totalScore: sum(player.scores),
    totalStrokes: sum(player.strokes),
    courseLabels: ['A'],
    roundId: 1
  };
}